import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import {
  Cpu,
  FileText,
  Briefcase,
  Paperclip,
  ArrowRight,
  Check,
  Zap,
  Terminal,
  BrainCircuit,
  Radio,
  Calendar,
  Rocket,
} from 'lucide-react';
import { useCortexAnalyst, Stage } from '../hooks/useCortexAnalyst';

type AgentColor = 'cyan' | 'purple' | 'amber';

interface AgentBadge {
  name: string;
  role: string;
  color: AgentColor;
  icon: typeof Cpu;
}

const WORKFLOW_STEPS = [
  { id: 'connect', label: 'Connect', icon: Radio },
  { id: 'analysis', label: 'Analysis', icon: BrainCircuit },
  { id: 'strategy', label: 'Strategy', icon: Calendar },
  { id: 'deploy', label: 'Deploy', icon: Rocket }
];

const agentByStage: Record<Stage, AgentBadge> = {
  analysis: { name: 'CORTEX ANALYST', role: 'Functional Analyst', color: 'cyan', icon: BrainCircuit },
  vision: { name: 'VECTOR VISION', role: 'Document Intelligence', color: 'purple', icon: FileText },
  sales: { name: 'SALES CLOSER', role: 'Commercial Agent', color: 'amber', icon: Briefcase },
};

const agentColorHex: Record<AgentColor, string> = {
  cyan: '#06b6d4',
  purple: '#a855f7',
  amber: '#f59e0b',
};

const COPY = {
  it: {
    overline: 'CORTEX.AI',
    title: 'L’analista funzionale virtuale',
    subtitle:
      'Parla con Cortex: capisce il tuo caso, raccoglie requisiti e sincronizza automaticamente il lead su CRM e email.',
    metrics: [
      { label: 'Tempo medio risposta', value: '47s', detail: 'Ultime 30 conversazioni' },
      { label: 'Lead salvati', value: '100%', detail: 'Sync automatico su Supabase' },
    ],
    badges: ['Claude Haiku 4.5', 'Supabase CRM', 'Microsoft Graph'],
    planHeader: 'Piani Vector',
    planLegend: 'Piano consigliato',
    planFallback: 'In attesa di dati',
    insightsTitle: 'Dati raccolti',
    quickPrompts: 'Domande suggerite',
    uploadLabel: 'Carica brief',
    uploadHint: 'PDF, XLSX o TXT (max 2MB)',
    processing: 'Cortex sta pensando…',
    inputPlaceholder: 'Descrivi la tua esigenza...',
    ctaPrimary: 'Genera proposta Vector',
    ctaSecondary: 'Prenota check-up',
    leadStatus: {
      idle: 'In ascolto',
      saving: 'Salvataggio lead…',
      success: 'Lead sincronizzato',
      error: 'Errore CRM',
    },
    insightsFields: {
      name: 'Referente',
      email: 'Email',
      company: 'Azienda',
      industry: 'Settore',
      budget: 'Budget',
      timeline: 'Timeline',
      stack: 'Stack attuale',
      painPoints: 'Pain',
      kpi: 'KPI',
      plan: 'Piano suggerito',
    },
    planMap: {
      foundation: 'Managed Cloud',
      launch: 'Automation Suite',
      vector: 'System Integration',
    },
    leadErrorFallback: 'Verifica la connessione o riprova più tardi.',
    uploadTooBig: 'File troppo grande (max 2MB).',
    uploadSuccess:
      'Ho caricato il documento. Consideralo per il workflow e chiedimi le parti mancanti se servono.',
    uploadUnsupported:
      'Ho caricato il documento, non posso inviare il contenuto ma descrivilo nella conversazione.',
    planCards: {
      foundation: {
        title: 'MANAGED CLOUD',
        subtitle: 'Infrastruttura & Sicurezza',
        bullets: [
          'Hosting Azure High-Performance (Italy North)',
          'Microsoft 365 Tenant Management (Email & Cloud)',
          'Gestione Tecnica DNS & Certificati SSL',
          'Setup Tecnico Google Business Profile',
          'Monitoraggio Uptime 24/7 & Backup',
          'Assistenza Sistemistica Diretta',
        ],
      },
      launch: {
        title: 'AUTOMATION SUITE',
        subtitle: 'Crescita Automatica',
        bullets: [
          'Tutto ciò che è in Managed Cloud',
          'VoIP Missed-Call Handler (SMS Back automatico)',
          'CRM Data Pipeline (Database Clienti Centralizzato)',
          'WhatsApp Business API Bot (Livello 1)',
          'Social API Sync (Distribuzione contenuti)',
          'Review Booster Workflow',
        ],
      },
      vector: {
        title: 'SYSTEM INTEGRATION',
        subtitle: 'Sviluppo Custom & AI',
        bullets: [
          'Sviluppo Web App & Portali Clienti (React/Next.js)',
          'AI Staffing Solutions (Agenti Vocali Vapi/OpenAI)',
          'Integrazione ERP & Magazzino',
          'Digital Transformation Consulting',
          'SLA Enterprise Dedicato',
        ],
      },
    },
  },
  en: {
    overline: 'CORTEX.AI',
    title: 'Your virtual functional analyst',
    subtitle:
      'Talk to Cortex: it captures requirements, proposes next steps and syncs the lead with CRM + email automatically.',
    metrics: [
      { label: 'Avg. response time', value: '47s', detail: 'Last 30 sessions' },
      { label: 'Leads captured', value: '100%', detail: 'Supabase auto-sync' },
    ],
    badges: ['Claude Haiku 4.5', 'Supabase CRM', 'Microsoft Graph'],
    planHeader: 'Vector plans',
    planLegend: 'Suggested plan',
    planFallback: 'Waiting for inputs',
    insightsTitle: 'Captured data',
    quickPrompts: 'Suggested prompts',
    uploadLabel: 'Upload brief',
    uploadHint: 'PDF, XLSX or TXT (max 2MB)',
    processing: 'Cortex is thinking…',
    inputPlaceholder: 'Describe your needs...',
    ctaPrimary: 'Generate Vector proposal',
    ctaSecondary: 'Book check-up',
    leadStatus: {
      idle: 'Listening',
      saving: 'Saving lead…',
      success: 'Lead synced',
      error: 'CRM error',
    },
    insightsFields: {
      name: 'Contact',
      email: 'Email',
      company: 'Company',
      industry: 'Industry',
      budget: 'Budget',
      timeline: 'Timeline',
      stack: 'Current stack',
      painPoints: 'Pain',
      kpi: 'KPI',
      plan: 'Suggested plan',
    },
    planMap: {
      foundation: 'Managed Cloud',
      launch: 'Automation Suite',
      vector: 'System Integration',
    },
    leadErrorFallback: 'Check the connection or try again.',
    uploadTooBig: 'File too large (max 2MB).',
    uploadSuccess:
      'Document uploaded. Use it in the workflow and ask me if any section is missing.',
    uploadUnsupported:
      'Document uploaded. I cannot read the binary but mention its contents in the chat.',
    planCards: {
      foundation: {
        title: 'MANAGED CLOUD',
        subtitle: 'Infrastructure & Security',
        bullets: [
          'Azure High-Performance Hosting (Italy North)',
          'Microsoft 365 Tenant Management (Email & Cloud)',
          'DNS & SSL technical governance',
          'Google Business Profile technical setup',
          '24/7 uptime monitoring & backups',
          'Direct systems engineering support',
        ],
      },
      launch: {
        title: 'AUTOMATION SUITE',
        subtitle: 'Autopilot Growth',
        bullets: [
          'Everything in Managed Cloud',
          'VoIP Missed-Call Handler (auto SMS back)',
          'CRM Data Pipeline (Central Customer DB)',
          'WhatsApp Business API Bot (Level 1)',
          'Social API Sync (Content Distribution)',
          'Review Booster Workflow',
        ],
      },
      vector: {
        title: 'SYSTEM INTEGRATION',
        subtitle: 'Custom Build & AI',
        bullets: [
          'Custom Web Apps & Portals (React/Next.js)',
          'AI Staffing Solutions (Voice Agents via Vapi/OpenAI)',
          'ERP & Warehouse Integrations',
          'Digital Transformation Consulting',
          'Dedicated Enterprise SLA',
        ],
      },
    },
  },
};

type MessageBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] };

const stripMarkdown = (line: string) =>
  line
    .replace(/^#+\s*/, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();

const parseMessageContent = (content: string): MessageBlock[] => {
  const lines = content.replace(/\r/g, '').split('\n');
  const blocks: MessageBlock[] = [];
  let currentList: string[] = [];

  const flushList = () => {
    if (currentList.length) {
      blocks.push({ type: 'list', items: [...currentList] });
      currentList = [];
    }
  };

  lines.forEach((line) => {
    const bulletMatch = line.trim().match(/^[-*•]\s+(.*)$/);
    if (bulletMatch) {
      currentList.push(stripMarkdown(bulletMatch[1]));
      return;
    }

    const cleaned = stripMarkdown(line);
    if (!cleaned) {
      flushList();
      return;
    }

    flushList();
    blocks.push({ type: 'paragraph', text: cleaned });
  });

  flushList();

  if (blocks.length === 0 && content.trim()) {
    return [{ type: 'paragraph', text: stripMarkdown(content) }];
  }

  return blocks;
};

const renderMessageBlocks = (content: string) =>
  parseMessageContent(content).map((block, idx) => {
    if (block.type === 'list') {
      return (
        <ul key={`list-${idx}`} className="ml-4 space-y-2 my-3">
          {block.items.map((item, subIdx) => (
            <li key={`list-${idx}-${subIdx}`} className="flex items-start gap-2 text-sm text-slate-300">
              <span className="w-1 h-1 rounded-full bg-cyan-400 mt-2 shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={`p-${idx}`} className="text-sm text-slate-200 leading-relaxed mb-2 last:mb-0 font-light">
        {block.text.split(/(\[(?:ACTION|APPOINTMENT_TIME):[^\]]*\])/g).map((part, i) => {
          const actionMatch = part.match(/^\[ACTION:([^\]]*)\]$/);
          const timeMatch = part.match(/^\[APPOINTMENT_TIME:([^\]]*)\]$/);
          
          if (actionMatch) {
            const action = actionMatch[1].trim();
            
            // Hide NULL or empty actions
            if (!action || action === 'NULL') return null;

            let label = action;
            let color = "bg-blue-500/20 text-blue-300 border-blue-500/30";
            
            if (action === 'GATHER_INFO') {
              label = "Gathering Intelligence";
              color = "bg-amber-500/20 text-amber-300 border-amber-500/30";
            } else if (action === 'BOOK_MEETING') {
              label = "Scheduling Phase";
              color = "bg-purple-500/20 text-purple-300 border-purple-500/30";
            } else if (action === 'CLOSE_DEAL') {
              label = "Finalizing Agreement";
              color = "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
            }

            return (
              <span key={i} className={`inline-flex items-center gap-1.5 px-2 py-0.5 mx-1 rounded text-[10px] font-mono uppercase tracking-wider border ${color} select-none align-middle`}>
                <BrainCircuit size={10} />
                {label}
              </span>
            );
          }
          
          if (timeMatch) {
            const time = timeMatch[1].trim();
            return (
              <span key={i} className="inline-flex items-center gap-1.5 px-2 py-0.5 mx-1 rounded text-[10px] font-mono uppercase tracking-wider border bg-cyan-500/20 text-cyan-300 border-cyan-500/30 select-none align-middle">
                <Calendar size={10} />
                {time.includes('T') ? new Date(time).toLocaleString() : time}
              </span>
            );
          }
          
          return part;
        })}
      </p>
    );
  });

const CortexShowcase: React.FC = () => {
  const { i18n } = useTranslation();
  const locale: 'it' | 'en' = i18n.language.startsWith('it') ? 'it' : 'en';
  const text = COPY[locale];
  const {
    messages,
    stage,
    insights,
    isThinking,
    error,
    leadStatus,
    leadError,
    suggestions,
    sendMessage,
    requestProposal,
  } = useCortexAnalyst();

  const [inputValue, setInputValue] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const planCards = text.planCards as Record<'foundation' | 'launch' | 'vector', { title: string; subtitle: string; bullets: string[] }>;

  // Calculate current workflow step based on message history
  const currentStepIndex = useMemo(() => {
    // Check for specific actions in history first
    for (let i = messages.length - 1; i >= 0; i--) {
      const content = messages[i].content;
      if (content.includes('[ACTION:CLOSE_DEAL]')) return 3;
      if (content.includes('[ACTION:BOOK_MEETING]')) return 3;
      // If we are gathering info, we are definitely in Analysis (1)
      if (content.includes('[ACTION:GATHER_INFO]')) return 1;
    }
    
    // Fallback to stage if no recent action found
    if (stage === 'sales') return 3;
    if (stage === 'vision') return 2;
    if (stage === 'analysis') {
      // If we are in analysis stage, check if user has sent at least one message
      const hasUserMessage = messages.some(m => m.role === 'user');
      return hasUserMessage ? 1 : 0;
    }
    
    return 0;
  }, [messages, stage]);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTo({
        top: chatScrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, isThinking]);

  const activeAgent = agentByStage[stage];

  const handleSend = async () => {
    if (!inputValue.trim() || isThinking) return;
    const value = inputValue.trim();
    setInputValue('');
    setLocalError(null);
    await sendMessage(value);
  };

  const handlePromptClick = async (prompt: string) => {
    if (isThinking) return;
    await sendMessage(prompt);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    if (file.size > 2 * 1024 * 1024) {
      setLocalError(text.uploadTooBig);
      return;
    }
    setLocalError(null);

    const isExcel = /\.(xlsx|xls|csv)$/i.test(file.name);
    const isTextual =
      !isExcel &&
      (file.type.startsWith('text/') || /\.txt$|\.md$|\.json$|\.log$/i.test(file.name));

    try {
      if (isExcel) {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        // Limit to first 50 rows to avoid token limits
        const json = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }).slice(0, 50);
        const csv = json.map((row: any) => row.join(',')).join('\n');
        
        const message =
          locale === 'it'
            ? `Ho caricato il file Excel/CSV ${file.name}. Ecco le prime righe:\n\n${csv}\n\nAnalizza questi dati per il workflow.`
            : `I uploaded the Excel/CSV file ${file.name}. Here are the first rows:\n\n${csv}\n\nAnalyze this data for the workflow.`;
        
        await sendMessage(message);
        setLocalError(text.uploadSuccess);
      } else if (isTextual) {
        const raw = await file.text();
        const snippet = raw.slice(0, 3600);
        const message =
          locale === 'it'
            ? `Ho caricato il documento ${file.name}. Ecco un estratto:\n\n${snippet}\n\nUsa queste informazioni per aggiornare il workflow e chiedi ciò che manca.`
            : `I uploaded ${file.name}. Here is an excerpt:\n\n${snippet}\n\nUse it to update the workflow and ask me what is missing.`;
        await sendMessage(message);
        setLocalError(text.uploadSuccess);
      } else {
        const note =
          locale === 'it'
            ? `Ho caricato il documento ${file.name} (${file.type || 'file'}). Non posso condividere il contenuto binario ma consideralo nel workflow.`
            : `I uploaded ${file.name} (${file.type || 'file'}). I cannot share the binary but please consider it in the workflow.`;
        await sendMessage(note);
        setLocalError(text.uploadUnsupported);
      }
    } catch (err) {
      console.error('Upload error', err);
      setLocalError(text.leadErrorFallback);
    }
  };

  const formatInsightValue = (value?: string | null) =>
    value ? value.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim() : undefined;

  const insightList = useMemo(() => {
    const fields = text.insightsFields;
    return [
      { label: fields.name, value: formatInsightValue(insights.name) },
      { label: fields.email, value: formatInsightValue(insights.email) },
      { label: fields.company, value: formatInsightValue(insights.company) },
      { label: fields.industry, value: formatInsightValue(insights.industry) },
      { label: fields.budget, value: formatInsightValue(insights.budget) },
      { label: fields.timeline, value: formatInsightValue(insights.timeline) },
      { label: fields.stack, value: formatInsightValue(insights.stack) },
      { label: fields.painPoints, value: formatInsightValue(insights.painPoints) },
      { label: fields.kpi, value: formatInsightValue(insights.kpi) },
    ].filter((item) => item.value); // Only show filled fields
  }, [insights, text]);

  const leadBadge = useMemo(() => {
    switch (leadStatus) {
      case 'saving':
        return { label: text.leadStatus.saving, className: 'text-amber-400 border-amber-500/30 bg-amber-500/10' };
      case 'success':
        return { label: text.leadStatus.success, className: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' };
      case 'error':
        return {
          label: leadError || text.leadStatus.error,
          className: 'text-red-400 border-red-500/30 bg-red-500/10',
        };
      default:
        return { label: text.leadStatus.idle, className: 'text-slate-500 border-slate-800 bg-slate-900/50' };
    }
  }, [leadStatus, leadError, text]);

  const agentColor = agentColorHex[activeAgent.color];

  return (
    <section id="cortex-ai" className="relative bg-[#03050b] text-white py-24 overflow-hidden selection:bg-cyan-500/30">
      {/* Ambient Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-cyan-500/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-purple-500/5 blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] font-mono uppercase tracking-[0.3em] text-cyan-300 backdrop-blur-sm"
          >
            <Cpu size={14} />
            {text.overline}
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold tracking-tight bg-gradient-to-b from-white via-white to-slate-500 bg-clip-text text-transparent"
          >
            {text.title}
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-400 text-lg font-light leading-relaxed"
          >
            {text.subtitle}
          </motion.p>
        </div>

        {/* Main Interface Grid */}
        <div className="grid lg:grid-cols-[240px,1fr,240px] xl:grid-cols-[320px,1fr,300px] gap-4 lg:gap-6 xl:gap-8 items-start">
          
          {/* Left: Plans & Context */}
          <motion.aside 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6 hidden lg:block"
          >
            {/* Dynamic Plan Selection */}
            <div className="bg-[#0A0B10]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-1 overflow-hidden">
              <div className="p-5 pb-2">
                <h3 className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1">{text.planHeader}</h3>
                <p className="text-sm text-slate-300">{insights.plan ? text.planMap[insights.plan] : text.planFallback}</p>
              </div>
              <div className="space-y-1 p-1">
                {(['foundation', 'launch', 'vector'] as const).map((planId) => {
                  const plan = planCards[planId];
                  const isActive = insights.plan === planId;
                  return (
                    <div
                      key={planId}
                      className={`relative group p-4 rounded-2xl border transition-all duration-300 cursor-default ${
                        isActive 
                          ? 'bg-cyan-950/20 border-cyan-500/30 shadow-[0_0_30px_-10px_rgba(6,182,212,0.15)]' 
                          : 'bg-transparent border-transparent hover:bg-white/5 hover:border-white/5'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-[10px] font-bold tracking-[0.2em] uppercase ${isActive ? 'text-cyan-400' : 'text-slate-500'}`}>
                          {plan.title}
                        </span>
                        {isActive && (
                          <motion.span 
                            layoutId="activePlan"
                            className="flex h-2 w-2 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.8)]" 
                          />
                        )}
                      </div>
                      <p className={`text-sm font-medium mb-3 ${isActive ? 'text-white' : 'text-slate-400'}`}>
                        {plan.subtitle}
                      </p>
                      {isActive && (
                        <motion.ul 
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="space-y-2"
                        >
                          {plan.bullets.map(bullet => (
                            <li key={bullet} className="flex gap-2 text-xs text-slate-300">
                              <Check size={12} className="text-cyan-500 mt-0.5 shrink-0" />
                              {bullet}
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Metrics */}
            <div className="grid grid-cols-2 gap-3">
               {text.metrics.map((metric) => (
                <div key={metric.label} className="bg-[#0A0B10]/50 border border-white/5 rounded-2xl p-4">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{metric.label}</div>
                  <div className="text-xl font-bold text-white">{metric.value}</div>
                </div>
               ))}
            </div>
          </motion.aside>

          {/* Center: Chat Interface */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex flex-col h-[700px] relative group"
          >
            {/* Glass Container */}
            <div className="absolute inset-0 bg-[#04060b]/90 backdrop-blur-2xl rounded-[32px] border border-white/10 shadow-2xl shadow-black/50" />
            <div className="absolute inset-0 rounded-[32px] border border-white/5 pointer-events-none mix-blend-overlay" />
            
            {/* Chat Header */}
            <div className="relative h-20 flex items-center justify-between px-8 border-b border-white/5 bg-white/[0.02]">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-cyan-500/20 blur-md rounded-full animate-pulse" />
                  <div 
                    className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-black border border-white/10 flex items-center justify-center text-cyan-400"
                    style={{ color: agentColor, borderColor: `${agentColor}30` }}
                  >
                    <activeAgent.icon size={20} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-white tracking-wide">{activeAgent.name}</h3>
                    <span className="flex h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
                  </div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">{activeAgent.role}</p>
                </div>
              </div>
              
              <div className="hidden sm:flex items-center gap-3 px-3 py-1.5 rounded-full bg-black/20 border border-white/5 text-[10px] font-mono text-slate-400">
                <Terminal size={12} />
                <span>CLAUDE-3-HAIKU-4.5</span>
              </div>
            </div>

            {/* Workflow Visualization */}
            <div className="w-full bg-white/[0.01] border-b border-white/5 pt-4 pb-6 px-4 backdrop-blur-md relative z-10">
                <div className="max-w-[90%] mx-auto relative">
                  {/* Background Line */}
                  <div className="absolute left-0 top-3.5 w-full h-0.5 bg-slate-800/50 rounded-full -z-10" />
                  
                  {/* Progress Line */}
                  <motion.div 
                    className="absolute left-0 top-3.5 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full -z-10 shadow-[0_0_10px_rgba(6,182,212,0.5)]"
                    initial={{ width: '0%' }}
                    animate={{ width: `${(currentStepIndex / (WORKFLOW_STEPS.length - 1)) * 100}%` }}
                    transition={{ duration: 0.8, ease: "easeInOut" }}
                  />

                  <div className="flex justify-between items-start">
                    {WORKFLOW_STEPS.map((step, index) => {
                      const isActive = index === currentStepIndex;
                      const isCompleted = index < currentStepIndex;
                      const Icon = step.icon;
                      
                      return (
                        <div key={step.id} className="flex flex-col items-center gap-2 relative group cursor-default">
                          <motion.div 
                            className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all duration-500 relative bg-[#04060b] ${
                              isActive 
                                ? 'border-cyan-500 text-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.4)]' 
                                : isCompleted 
                                  ? 'border-cyan-500/50 text-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.2)]'
                                  : 'border-slate-800 text-slate-700'
                            }`}
                            animate={{ 
                              scale: isActive ? 1.2 : 1,
                              borderColor: isActive ? '#06b6d4' : isCompleted ? 'rgba(6,182,212,0.5)' : '#1e293b'
                            }}
                          >
                            {isCompleted ? (
                              <Check size={12} strokeWidth={3} />
                            ) : (
                              <Icon size={12} />
                            )}
                            
                            {/* Pulse Effect for Active */}
                            {isActive && (
                              <>
                                <span className="absolute inset-0 rounded-full animate-ping bg-cyan-500/30 duration-1000" />
                                <span className="absolute inset-0 rounded-full bg-cyan-500/10 blur-md" />
                              </>
                            )}
                          </motion.div>
                          
                          <motion.span 
                            className={`text-[9px] font-mono uppercase tracking-wider absolute -bottom-6 whitespace-nowrap transition-colors duration-300 ${
                              isActive ? 'text-cyan-400 font-bold' : isCompleted ? 'text-slate-500' : 'text-slate-800'
                            }`}
                            animate={{ opacity: isActive || isCompleted ? 1 : 0.5 }}
                          >
                            {step.label}
                          </motion.span>
                        </div>
                      );
                    })}
                  </div>
                </div>
            </div>

            {/* Messages Area */}
            <div 
              ref={chatScrollRef} 
              className="relative flex-1 overflow-y-auto p-6 sm:p-8 space-y-8 scroll-smooth"
            >
              <AnimatePresence initial={false}>
                {messages.map((message) => {
                  const isAi = message.role === 'assistant';
                  const mappedAgent = isAi ? agentByStage[message.stage || stage] : null;
                  
                  return (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className={`flex flex-col ${isAi ? 'items-start' : 'items-end'}`}
                    >
                      <div className={`flex items-center gap-2 mb-2 text-[10px] uppercase tracking-wider font-bold ${isAi ? 'text-cyan-400' : 'text-slate-500'}`}>
                        {isAi ? mappedAgent?.name : 'You'}
                      </div>
                      
                      <div
                        className={`relative max-w-[90%] md:max-w-[80%] p-5 md:p-6 text-sm leading-relaxed shadow-xl backdrop-blur-md ${
                          isAi 
                            ? 'rounded-2xl rounded-tl-none bg-slate-900/80 border border-white/10 text-slate-200' 
                            : 'rounded-2xl rounded-tr-none bg-cyan-500/10 border border-cyan-500/20 text-white'
                        }`}
                      >
                        {renderMessageBlocks(message.content)}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
              
              {isThinking && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-3 text-slate-500 text-xs ml-2"
                >
                  <div className="flex gap-1">
                    <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1 }} className="w-1 h-1 bg-cyan-500 rounded-full" />
                    <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1, delay: 0.2 }} className="w-1 h-1 bg-cyan-500 rounded-full" />
                    <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1, delay: 0.4 }} className="w-1 h-1 bg-cyan-500 rounded-full" />
                  </div>
                  <span className="font-mono uppercase tracking-wider">{text.processing}</span>
                </motion.div>
              )}
              
              <div className="h-4" />
            </div>

            {/* Input Area */}
            <div className="relative p-6 pt-2">
               {/* Suggestions */}
              {suggestions.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4 overflow-x-auto pb-2 no-scrollbar">
                  {suggestions.map((prompt) => (
                    <motion.button
                      key={prompt}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      onClick={() => handlePromptClick(prompt)}
                      className="whitespace-nowrap px-4 py-2 text-xs rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-cyan-500/30 hover:text-cyan-300 transition-all"
                      disabled={isThinking}
                    >
                      {prompt}
                    </motion.button>
                  ))}
                </div>
              )}

              <div className="relative group">
                <div 
                  className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-cyan-500/20 opacity-0 group-hover:opacity-100 transition duration-500 blur-md" 
                />
                <div className="relative flex items-center bg-[#020305] rounded-2xl border border-white/10 shadow-lg transition-colors group-hover:border-white/20">
                  <button
                    onClick={handleUploadClick}
                    className="p-4 text-slate-500 hover:text-white transition-colors border-r border-white/5"
                    disabled={isThinking}
                    title={text.uploadLabel}
                  >
                    <Paperclip size={20} />
                  </button>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.md,.json,.csv,.xlsx,.xls"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(event) => setInputValue(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder={text.inputPlaceholder}
                    disabled={isThinking}
                    className="flex-1 bg-transparent border-none outline-none text-white px-4 py-5 placeholder-slate-600 text-sm font-light"
                    autoComplete="off"
                  />
                  
                  <button
                    onClick={handleSend}
                    disabled={isThinking || !inputValue.trim()}
                    className="p-4 m-1 rounded-xl text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all hover:bg-white/10"
                    style={{ color: inputValue.trim() ? agentColor : undefined }}
                  >
                    <ArrowRight size={20} />
                  </button>
                </div>
              </div>
              <div className="mt-2 flex justify-between px-2">
                 <p className="text-[10px] text-slate-600">{text.uploadHint}</p>
                 {(error || localError) && (
                  <span className="text-[10px] text-red-400">{error || localError}</span>
                 )}
              </div>
            </div>
          </motion.div>

          {/* Right: Live Insights */}
          <motion.aside 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
             {/* Lead Status Card */}
            <div className="bg-[#0A0B10]/80 backdrop-blur border border-white/10 rounded-3xl p-6">
               <div className="flex items-center justify-between mb-6">
                 <h3 className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">{text.insightsTitle}</h3>
                 <div className={`px-3 py-1 rounded-full border text-[9px] uppercase tracking-widest font-bold flex items-center gap-2 ${leadBadge.className}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${leadStatus === 'saving' ? 'animate-pulse bg-amber-400' : leadStatus === 'success' ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                    {leadBadge.label}
                 </div>
               </div>

               <div className="space-y-4">
                 {/* Filled Fields */}
                 {insightList.map((item) => (
                   <div key={item.label} className="group">
                     <p className="text-[9px] uppercase tracking-widest text-slate-600 mb-1 group-hover:text-cyan-500 transition-colors">{item.label}</p>
                     <p className="text-sm text-slate-200 font-medium truncate border-b border-white/5 pb-1">{item.value}</p>
                   </div>
                 ))}
                 
                 {insightList.length === 0 && (
                   <div className="text-center py-8 text-slate-600">
                     <Zap size={24} className="mx-auto mb-2 opacity-20" />
                     <p className="text-xs">In attesa di dati...</p>
                   </div>
                 )}
               </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={requestProposal}
                disabled={isThinking}
                className="w-full group relative overflow-hidden rounded-2xl bg-cyan-500 p-px transition-all hover:shadow-[0_0_40px_-10px_rgba(6,182,212,0.5)]"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] group-hover:animate-[shimmer_1.5s_infinite]" />
                <div className="relative bg-black/20 backdrop-blur-sm rounded-2xl px-6 py-4 text-center">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-white group-hover:text-cyan-50 shadow-black drop-shadow-md">
                    {text.ctaPrimary}
                  </span>
                </div>
              </button>
              
              <Link
                to="/contact"
                className="block w-full text-center px-6 py-4 rounded-2xl border border-white/10 hover:bg-white/5 transition-colors"
              >
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-300">
                  {text.ctaSecondary}
                </span>
              </Link>
            </div>
          </motion.aside>

        </div>
      </div>
    </section>
  );
};

export default CortexShowcase;
